import React, { useEffect, useState } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { currentUser, InfegyAccount } from "system/InfegyUser";
import { getAccountUsage, getBillingOrder, UsageTerm } from "../ApiUsagePage";
import { TextLoader } from "components/apiHandling/TextLoader";
import APIRequest from "system/APIRequest";
import PrePurchaseUsageCard from "../components/PrePurchaseUsageCard";
import { getUsageCategoryList, Usage, UsageCategoryKey } from "../types/Usage";
import { ApiPricingModel, BillingOrder } from "../types/BillingOrder";
import { BillingUsageEmail } from "../types/BillingUsageEmail";
import DateRangeLabel from "../components/DateRangeLabel";

const UsageCategoryList = getUsageCategoryList();

interface PrePurchaseUsageProps {
  account: InfegyAccount;
  currentTerm: UsageTerm;
  visible: boolean;
}

export interface PricingModelUsage {
  usageCategory: UsageCategoryKey;
  pricingModel: ApiPricingModel;
  usage: number;
}

export default function PrePurchaseUsage(props: PrePurchaseUsageProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const thisUser = currentUser();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string>("");
  const [pricingModelUsage, setPricingModelUsage] = useState<Array<PricingModelUsage>>([]);
  const [billingUsageEmails, setBillingUsageEmails] = useState<Array<BillingUsageEmail>>([]);

  async function getBillingUsageEmails(account: InfegyAccount, currentTerm: UsageTerm): Promise<Array<BillingUsageEmail>> {
    const queryParams = {
      admin: "1",
      q: JSON.stringify([{
        id: "account_id",
        op: "=",
        value: account.id
      }, {
        id: "created_at",
        op: ">=",
        value: currentTerm.term_start
      }])
    };
    const response: any = await APIRequest(`obj/billing-api-allocation-usage-email-log`, queryParams);
    return response.output || [];
  }

  function getPricingModelUsage(billingOrder: BillingOrder, accountUsage: Usage): Array<PricingModelUsage> {
    const result: Array<PricingModelUsage> = [];
    for (const category of UsageCategoryList) {
      const allotment = billingOrder.data.api_pricing[category].allotment.count;
      if (allotment > 0) {
        result.push({
          usageCategory: category,
          pricingModel: billingOrder.data.api_pricing[category],
          usage: accountUsage.total.cost[category] || 0
        });
      }
    }
    return result;
  }

  async function load() {
    setIsLoading(true);
    setLoadError("");
    setPricingModelUsage([]);
    setBillingUsageEmails([]);

    try {
      const accountUsage = await getAccountUsage(props.currentTerm, props.account.id);
      
      let billingOrder: BillingOrder | null = null;
      let pricingModelUsage: Array<PricingModelUsage> = [];
      let billingUsageEmails: Array<BillingUsageEmail> = [];
      if (props.account.billing_order_id) {
        billingOrder = await getBillingOrder(props.account.billing_order_id, !!thisUser?.admin);

        pricingModelUsage = getPricingModelUsage(billingOrder, accountUsage);

        billingUsageEmails = await getBillingUsageEmails(props.account, props.currentTerm);
      }
      setPricingModelUsage(pricingModelUsage);
      setBillingUsageEmails(billingUsageEmails);
    } catch (error: any) {
      if (error.code) {
        // This was an error from the API.  Display it as such.
        setLoadError(`${error.message}: ${error.server_message}`);
      } else {
        // This was an error from the UI.
        setLoadError(`${i18n.t("api-usage-page.error-loading")} ${error}`);
      }
    }

    setIsLoading(false);
  }

  useEffect(() => {
    load();
  }, [props.currentTerm, props.account]);

  return (
    <Stack
      sx={{
        display: props.visible ? "auto" : "none",
        gap: 1
      }}>
      {isLoading &&
        <TextLoader />
      }
      {!isLoading &&
        <Stack sx={{
          gap: 1
        }}>
          <Box>
            <Typography variant="h3" sx={{
              width: "100%"
            }}>{i18n.t("pre-purchase-usage.pre-purchase-heading")}</Typography>
            {!loadError &&
              <DateRangeLabel end={props.currentTerm.term_end} start={props.currentTerm.term_start} />
            }
          </Box>
          {loadError &&
            <Alert severity="error" variant="outlined">{loadError}</Alert>
          }
          {!loadError && pricingModelUsage.length === 0 &&
            <Alert severity="info" variant="outlined">{i18n.t("pre-purchase-usage.no-pre-purchase-credits-info")}</Alert>
          }
          {!loadError && pricingModelUsage.length > 0 &&
            <>
              {pricingModelUsage.map((model) => {
                const emailNotifications = billingUsageEmails.filter((notification) => notification.type === model.usageCategory);
                return <PrePurchaseUsageCard key={model.usageCategory} emailNotifications={emailNotifications} pricingModelUsage={model} />
              })}
            </>
          }
        </Stack>
      }
    </Stack>
  );
}